/**
 * Turns text into what it reads as, so disguised words look like the words they are:
 * look-alike letters from other alphabets, accents, digits and symbols used as letters,
 * and the separators people put between letters. Every letter kept remembers where it came
 * from in the original, so a match can be masked in place.
 */

/** Letters from other alphabets that look like Latin ones (Unicode's confusables, the common ones). */
const LOOK_ALIKES: Record<string, string> = {
  // Cyrillic
  а: 'a',
  в: 'b',
  е: 'e',
  ё: 'e',
  з: '3',
  к: 'k',
  м: 'm',
  н: 'h',
  о: 'o',
  п: 'n',
  р: 'p',
  с: 'c',
  т: 't',
  у: 'y',
  х: 'x',
  ѕ: 's',
  і: 'i',
  ї: 'i',
  ј: 'j',
  ԁ: 'd',
  ԛ: 'q',
  ԝ: 'w',
  ӏ: 'l',
  ь: 'b',
  // Greek
  α: 'a',
  β: 'b',
  γ: 'y',
  ε: 'e',
  η: 'n',
  ι: 'i',
  κ: 'k',
  μ: 'u',
  ν: 'v',
  ο: 'o',
  ρ: 'p',
  σ: 'o',
  ς: 'c',
  τ: 't',
  υ: 'u',
  χ: 'x',
  ω: 'w',
  // Latin letters that stand in for plain ones
  ı: 'i',
  ɡ: 'g',
  ł: 'l',
  ø: 'o',
  đ: 'd',
  ħ: 'h',
  ß: 'ss',
  æ: 'ae',
  œ: 'oe',
  ð: 'd',
  þ: 'p',
};

/** Digits and symbols written in place of letters (`sh1t`, `@ss`, `$lut`). */
const STAND_INS: Record<string, string> = {
  '0': 'o',
  '1': 'i',
  '3': 'e',
  '4': 'a',
  '5': 's',
  '7': 't',
  '8': 'b',
  '9': 'g',
  '@': 'a',
  $: 's',
  '!': 'i',
  '|': 'i',
  '+': 't',
  '€': 'e',
  '£': 'l',
};

/**
 * Letters people swap for each other to get past filters, read as one: `l` and `i` (and so
 * `1`), `v` and `u` (`fvck`). Words in the lists go through the same folding, so both sides
 * agree.
 */
const FOLDS: Record<string, string> = { l: 'i', v: 'u' };

/**
 * Characters with no width or look (soft hyphen, zero-width spaces and joiners, direction
 * marks, the byte-order mark…): removed without splitting a word.
 */
const INVISIBLE_RANGES: readonly [number, number][] = [
  [0xad, 0xad],
  [0x34f, 0x34f],
  [0x61c, 0x61c],
  [0x115f, 0x1160],
  [0x17b4, 0x17b5],
  [0x180e, 0x180e],
  [0x200b, 0x200f],
  [0x202a, 0x202e],
  [0x2060, 0x2064],
  [0x206a, 0x206f],
  [0x3164, 0x3164],
  [0xfeff, 0xfeff],
];
const isInvisible = (char: string) => {
  const code = char.codePointAt(0) ?? 0;
  return INVISIBLE_RANGES.some(([from, to]) => code >= from && code <= to);
};
const MARK = /\p{M}/u;

/** A digit run this long is a number (`Comet4821`), not letters in disguise. */
const NUMBER_RUN = 3;
/** Signs between two numbers: sizes, scores, sums and ratios. */
const NUMBER_SIGNS = new Set(['×', 'x', 'X', '*', '·', '/', ':', '-', '–', '−', '+', '=', '÷']);

export interface View {
  /** What the text reads as: a–z only. */
  text: string;
  /** For each letter of `text`, where it starts and ends in the original (UTF-16 offsets). */
  from: number[];
  to: number[];
  /** True where a word starts in `text`. Single letters in a row count as one word (`f u c k`). */
  wordStart: boolean[];
}

/** One letter's journey: what a single original character reads as. */
function readChar(char: string): string {
  let out = '';
  for (const piece of char.normalize('NFKD')) {
    if (MARK.test(piece) || isInvisible(piece)) continue;
    const lower = piece.toLowerCase();
    out += LOOK_ALIKES[lower] ?? lower;
  }
  return out;
}

const isUpper = (char: string) => char !== char.toLowerCase() && char === char.toUpperCase();
const isLower = (char: string) => char !== char.toUpperCase() && char === char.toLowerCase();
const isDigit = (char: string) => char >= '0' && char <= '9';

/** Folds plain words (from the lists) the same way as text, without the stand-ins. */
export function foldWord(word: string): string {
  return readView(word, false).text;
}

/** What `input` reads as, with where each letter came from and where its words start. */
export function readView(input: string, standIns = true): View {
  const chars: { char: string; at: number }[] = [];
  for (let at = 0; at < input.length; ) {
    const point = input.codePointAt(at) ?? 0;
    const char = String.fromCodePoint(point);
    chars.push({ char, at });
    at += char.length;
  }

  // Digits in a long run are a number; one or two among letters may be letters in disguise.
  // So are digits joined to other digits by a sign (`5 × 5`, `3:2`, `1-0`), else `a 5 × 5`
  // reads as `a s s`.
  const number = new Array<boolean>(chars.length).fill(false);
  const digitAt = (k: number) => isDigit(chars[k]?.char ?? '');
  const runEnd = (k: number) => {
    while (k < chars.length && digitAt(k)) k++;
    return k;
  };
  const skipSpaces = (k: number) => {
    while (chars[k]?.char === ' ') k++;
    return k;
  };
  for (let i = 0; i < chars.length; ) {
    if (!digitAt(i)) {
      i++;
      continue;
    }
    const j = runEnd(i);
    if (j - i >= NUMBER_RUN) number.fill(true, i, j);
    const sign = skipSpaces(j);
    const next = skipSpaces(sign + 1);
    if (NUMBER_SIGNS.has(chars[sign]?.char ?? '') && digitAt(next)) {
      number.fill(true, i, j);
      number.fill(true, next, runEnd(next));
    }
    i = j;
  }

  const view: View = { text: '', from: [], to: [], wordStart: [] };
  let gap = true;
  let previous = '';
  chars.forEach(({ char, at }, index) => {
    if (isInvisible(char)) return;
    const read = readChar(char);
    const letters = [...read]
      .map((piece) => (standIns && !number[index] ? (STAND_INS[piece] ?? piece) : piece))
      .filter((piece) => piece >= 'a' && piece <= 'z')
      .map((piece) => FOLDS[piece] ?? piece);
    if (!letters.length) {
      // A space, `_`, punctuation or a number: the next letter starts a word.
      if (read !== '') gap = true;
      previous = char;
      return;
    }
    // A capital after a small letter starts a word too (`SkyRunner`).
    const camel = isUpper(char) && isLower(previous);
    letters.forEach((letter, n) => {
      view.wordStart.push(n === 0 && (gap || camel || view.text.length === 0));
      view.text += letter;
      view.from.push(at);
      view.to.push(at + char.length);
    });
    gap = false;
    previous = char;
  });

  // `f u c k` and `f_u_c_k`: single letters in a row read as one word.
  const starts = [...view.wordStart];
  const single = (k: number) => starts[k] && (k + 1 >= starts.length || starts[k + 1]);
  for (let i = 1; i < starts.length; i++) {
    if (single(i) && single(i - 1)) view.wordStart[i] = false;
  }
  return view;
}
